/**
 * Cost S-curve: each measure's dated amounts, bucketed by month and
 * accumulated. The same idea as the DCS task S-curve (an estimate spread over
 * time against actuals, accumulated), but over cost and from dated records.
 *
 * `events` is { bud, act, inv, pd }, each a list of { date, amount }.
 *  - a measure with no dated amounts is left out of the chart;
 *  - before its first amount a curve sits at 0, and after its last it stops
 *    (null) rather than running flat into the future - Actual / Invoiced /
 *    Paid end at the data date, Budget runs to the end of its plan;
 *  - amounts with no usable date can't be placed, and are counted in
 *    `undated` so the page can say so.
 */

const MEASURES = ["bud", "act", "inv", "pd"];

export const monthIndex = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.getUTCFullYear() * 12 + date.getUTCMonth();
};

export const monthLabel = (index) =>
  new Date(Date.UTC(Math.floor(index / 12), index % 12, 1)).toLocaleDateString(
    "en-US",
    { month: "short", year: "2-digit", timeZone: "UTC" },
  );

export const buildSCurve = (events) => {
  const perMeasure = {};
  const undated = {};
  let first = Infinity;
  let last = -Infinity;

  MEASURES.forEach((key) => {
    const buckets = new Map();
    undated[key] = 0;

    (events?.[key] || []).forEach(({ date, amount }) => {
      const value = Number(amount);
      if (!Number.isFinite(value)) return;
      const index = monthIndex(date);
      if (index === null) {
        undated[key] += value;
        return;
      }
      buckets.set(index, (buckets.get(index) || 0) + value);
      first = Math.min(first, index);
      last = Math.max(last, index);
    });

    if (buckets.size > 0) perMeasure[key] = buckets;
  });

  const keys = Object.keys(perMeasure);
  if (keys.length === 0) {
    return { labels: [], series: {}, measures: [], undated };
  }

  const months = [];
  for (let index = first; index <= last; index += 1) months.push(index);

  const series = {};
  keys.forEach((key) => {
    const buckets = perMeasure[key];
    const lastOwn = Math.max(...buckets.keys());
    let running = 0;
    series[key] = months.map((index) => {
      running += buckets.get(index) || 0;
      return index > lastOwn ? null : running;
    });
  });

  return {
    labels: months.map(monthLabel),
    series,
    measures: MEASURES.filter((key) => series[key]),
    undated,
  };
};
